import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from './config';

/**
 * Every read this site performs, in one place.
 *
 * These mirror longreads-app/src/firebase/books.ts, and the access rules below
 * mirror its `_readAccessOf` / `_isEntryGated` exactly. That duplication is
 * deliberate and load-bearing: Firestore rules make the documents world
 * readable, so the free-preview and subscriber gating is enforced by the
 * CLIENT in both the app and here. If this file and the app disagree, the
 * looser of the two is what the world actually gets — so when the app's gating
 * changes, this must change with it.
 */

/** Normalizes older series docs, which only had requiresSubscription + postprivacy. */
export const readAccessOf = (series) => {
  if (series?.readAccess) return series.readAccess;
  if (series?.requiresSubscription) return 'subscribers';
  if (series?.postprivacy === 'private') return 'private';
  if (series?.postprivacy === 'matesonly') return 'friends';
  return 'public';
};

/**
 * Whether an entry is locked to an anonymous web reader.
 *
 * Nobody is ever signed in here, so `hasAccess` is permanently false and the
 * free-preview rule is all that opens anything: entry 0 of a non-private book
 * is readable, everything after it is locked unless the whole book is public.
 */
export const isEntryLockedOnWeb = (series, entry) => {
  const access = readAccessOf(series);
  if (access === 'public') return false;
  if (access === 'private') return true;
  return entry?.order !== 0;
};

/** A private book is not shown on the web at all, not even its cover. */
export const isSeriesPubliclyListable = (series) =>
  series?.status === 'active' && readAccessOf(series) !== 'private';

/**
 * An author can pause Long reads: their profile and writing stop appearing for
 * everyone else, while the account and the username stay theirs. Txty writes
 * the same `longReadsPaused` field, so pausing in either app pauses both.
 *
 * Checked on every public surface here — profile, book and entry — because a
 * paused author's work has to disappear from direct links too, not just from
 * the pages that happen to list them.
 */
export const isPaused = (profile) => profile?.longReadsPaused === true;

export const getUserByUsername = async (username) => {
  const snap = await getDocs(
    query(collection(db, 'users'), where('username', '==', username), limit(1))
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const getUserById = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getSeriesByAuthor = async (authorId) => {
  const author = await getUserById(authorId);
  if (isPaused(author)) return [];

  const snap = await getDocs(
    query(collection(db, 'book_series'), where('authorId', '==', authorId))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(isSeriesPubliclyListable)
    .sort(
      (a, b) =>
        (b.updatedAt?.seconds || b.createdAt?.seconds || 0) -
        (a.updatedAt?.seconds || a.createdAt?.seconds || 0)
    );
};

export const getSeries = async (seriesId) => {
  const snap = await getDoc(doc(db, 'book_series', seriesId));
  if (!snap.exists()) return null;
  const series = { id: snap.id, ...snap.data() };
  if (!isSeriesPubliclyListable(series)) return null;

  // A paused author's books vanish from direct links too, not just listings.
  const author = await getUserById(series.authorId);
  return isPaused(author) ? null : series;
};

export const getEntries = async (seriesId) => {
  const snap = await getDocs(collection(db, 'book_series', seriesId, 'entries'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((e) => e.status === 'published')
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

export const getEntry = async (seriesId, entryId) => {
  const snap = await getDoc(doc(db, 'book_series', seriesId, 'entries', entryId));
  if (!snap.exists()) return null;
  const entry = { id: snap.id, ...snap.data() };
  return entry.status === 'published' ? entry : null;
};

export const getComments = async (seriesId, entryId) => {
  const snap = await getDocs(collection(db, 'book_series', seriesId, 'comments'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.status !== 'deleted')
    .filter((c) => (entryId ? c.entryId === entryId : !c.entryId))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

/** Single source of truth for how an entry is labelled, as in the app. */
const ENTRY_TYPE_LABELS = {
  introduction: 'Introduction',
  chapter: 'Chapter',
  update: 'Update',
  note: 'Note',
  announcement: 'Announcement',
};

export const entryLabel = (entry) => {
  if (!entry) return 'Entry';
  if (entry.entryType === 'chapter') {
    return entry.chapterNumber ? `Chapter ${entry.chapterNumber}` : 'Chapter';
  }
  if (entry.entryType === 'custom') return entry.customTypeLabel || 'Entry';
  return ENTRY_TYPE_LABELS[entry.entryType] || 'Entry';
};
